import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CaretLeft, Coins, Wallet as WalletIcon, ArrowCounterClockwise, Gift } from '@phosphor-icons/react';
import { getWalletDashboard } from '../../services/customerApi';
import { PageWrap } from './CustomerApp';
import { toast } from 'sonner';

export default function CustomerWallet() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await getWalletDashboard();
        setData(res.data);
      } catch (e) {
        toast.error('Could not load wallet');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <PageWrap>
        <div className="p-6 text-center text-gray-400 text-sm">Loading...</div>
      </PageWrap>
    );
  }

  if (!data) {
    return (
      <PageWrap>
        <div className="p-6 text-center text-gray-400 text-sm">Wallet data unavailable right now.</div>
      </PageWrap>
    );
  }

  // Merge + sort points and wallet activity into one feed, newest first.
  const activity = [
    ...(data.points_activity || []).map((a) => ({
      kind: a.type === 'EARN' ? 'earn' : a.type === 'ADJUST' ? (a.points < 0 ? 'clawback' : 'earn') : 'redeem',
      label: a.type === 'EARN' ? `Earned on order #${a.order_number || ''}` : a.type === 'ADJUST' ? (a.reason || 'Manual adjustment') : 'Points redeemed',
      amount: `${a.points > 0 ? '+' : ''}${a.points} pts`,
      positive: a.points > 0,
      date: a.created_at,
    })),
    ...(data.wallet_activity || []).map((a) => ({
      kind: a.type === 'CREDIT' ? 'refund' : 'clawback',
      label: a.note || (a.source === 'clawback' ? 'Wallet clawback' : 'Wallet credit'),
      amount: `${a.delta > 0 ? '+' : ''}₹${Math.abs(a.delta).toFixed(2)}`,
      positive: a.delta > 0,
      date: a.created_at,
    })),
  ].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 12);

  return (
    <PageWrap>
      <div className="min-h-screen bg-[#1c1310] text-[#fbf6ee] pb-8">
        <div className="px-5 pt-6 pb-4 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-1">
            <CaretLeft size={22} />
          </button>
          <h1 className="text-lg font-bold">Wallet &amp; Points</h1>
        </div>

        <div className="px-5 flex gap-3">
          <div className="flex-1 rounded-2xl p-4 bg-gradient-to-br from-[#3a2a1a] to-[#241a12] border border-[#d4a24c]/25">
            <div className="text-[11px] uppercase tracking-wide font-bold text-[#d4a24c]/70 mb-2">Loyalty points</div>
            <div className="text-2xl font-bold text-[#d4a24c]">{data.points.total_balance}</div>
            <div className="text-xs opacity-50 mt-1">≈ ₹{data.points.redeemable_value_rupees} value</div>
          </div>
          <div className="flex-1 rounded-2xl p-4 bg-gradient-to-br from-[#2e1f1c] to-[#1e1512] border border-[#c4432b]/30">
            <div className="text-[11px] uppercase tracking-wide font-bold text-[#c4432b]/70 mb-2">Wallet credit</div>
            <div className="text-2xl font-bold text-[#e8836b]">₹{data.wallet_balance.toFixed(2)}</div>
            <div className="text-xs opacity-50 mt-1">From refunds &amp; credits</div>
          </div>
        </div>

        <div className="mx-5 mt-4 rounded-xl bg-[#4a6741]/15 border border-[#4a6741]/35 px-4 py-3 text-[12.5px] flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-[#7fa571] flex-shrink-0" />
          <span>
            Earning <b className="text-[#9fc48f]">{data.payback.online_percent}% back</b> online,{' '}
            <b className="text-[#9fc48f]">{data.payback.outlet_percent}% back</b> at the outlet
          </span>
        </div>

        <div className="px-5 mt-6 mb-2 text-[13px] uppercase tracking-wide font-bold text-[#fbf6ee]/50">
          Recent activity
        </div>
        <div className="px-5">
          {activity.length === 0 ? (
            <p className="text-sm opacity-40 py-4">No activity yet — place an order to start earning.</p>
          ) : (
            activity.map((a, i) => {
              const Icon = a.kind === 'refund' ? ArrowCounterClockwise : a.kind === 'redeem' ? Coins : a.kind === 'clawback' ? WalletIcon : Gift;
              return (
                <div key={i} className="flex items-center gap-3 py-3 border-b border-white/5 last:border-0">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    a.kind === 'refund' ? 'bg-[#c4432b]/20' : a.kind === 'clawback' ? 'bg-[#c4432b]/30' : a.kind === 'redeem' ? 'bg-[#d4a24c]/15' : 'bg-[#4a6741]/20'
                  }`}>
                    <Icon size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13.5px] font-medium truncate">{a.label}</div>
                    <div className="text-[11px] opacity-40 mt-0.5">
                      {new Date(a.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                  <div className={`text-[13.5px] font-bold ${a.positive ? 'text-[#9fc48f]' : 'text-[#e8836b]'}`}>
                    {a.amount}
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="px-5 mt-6">
          <p className="text-[10.5px] opacity-35 leading-relaxed text-center">
            Wallet credit applies only to online orders. Points can be redeemed online or at the outlet, subject to order minimums.
          </p>
        </div>
      </div>
    </PageWrap>
  );
}
